import type { FormInstance } from 'antd';
import { Form } from 'antd';
import classNames from 'classnames';
import type { FieldData } from 'rc-field-form/es/interface';
import React, {
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { mapToRoomResponse } from 'app/hooks/data/core/extranet/inventory/mapping/room';
import { useSaveRooms } from 'app/hooks/data/core/extranet/inventory/rooms/useRooms';
import { ROOM_OPENING_DISCOUNT_MODE } from 'app/typings/core/extranet/inventory/enums/RoomOpeningDiscountMode';
import type { RoomOpeningDiscountMode } from 'app/typings/core/extranet/inventory/enums/RoomOpeningDiscountMode';

import { inventoryStore } from '../../../InventoryStore';
import type {
  InventoryExperiences,
  InventoryPacks,
  InventoryRooms,
} from '../../../InventoryStore';
import {
  useInventoryForm,
  useInventoryState,
  useTabInventoryActions,
  useTabInventoryState,
} from '../../../hooks';
import { useFilteredRooms } from '../../../hooks/useFilteredRooms';
import {
  getChangedFormFields,
  getModifiedValues,
  mergeInventoryObjects,
  retrieveDataFromNamePath,
  retrieveFormValues,
} from '../../../utils';
import { mapFormFieldToEditedField } from '../../../utils/editedFields';
import { updateRoomFieldsForDate } from '../../../utils/inventoryUpdates';
import { EmptyState } from '../../EmptyState/EmptyState';
import { RoomTabSkeleton } from '../../Skeletons/RoomTabSkeleton/RoomTabSkeleton';
import { isEnabledByMessage } from '../tabViewUtils';

import { PricingRuleAlertModal } from './PricingRuleAlertModal/PricingRuleAlertModal';
import styles from './RoomsTab.module.scss';
import { RoomTableItem } from './_components/RoomTableItem';

export const RoomsTab: React.FC = () => {
  const [form] = Form.useForm();
  const { hotelInfo } = useContext(HotelContext);

  const { timePeriod } = useInventoryState();

  const { sortedRooms, roomsCategories, isFetching } = useFilteredRooms();
  const { mutateAsync: saveRoomInventory } = useSaveRooms(
    hotelInfo?.id,
    timePeriod
  );

  const {
    inventoryRooms,
    inventory,
    dependencies,
    rawInventory,
    modifiedInventory,
    timePeriodStore,
    hasDeactivatedPricingRuleByEdit,
  } = useTabInventoryState();

  const {
    setForm,
    setModifiedInventory,
    addEditedRoomField,
    setHasDeactivatedPricingRuleByEdit,
    setIsEditMode,
  } = useTabInventoryActions();

  const storeForm = useInventoryForm();

  const [pendingSaveValues, setPendingSaveValues] =
    useState<InventoryRooms | null>(null);

  React.useLayoutEffect(() => {
    setForm(
      form as FormInstance<
        InventoryExperiences | InventoryRooms | InventoryPacks
      >
    );
  }, [form, setForm]);

  useLayoutEffect(() => {
    const mergedInventory = mergeInventoryObjects(
      inventory ?? {},
      timePeriodStore
    );

    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const inventoryRooms = retrieveFormValues(mergedInventory).inventoryRooms;

    form.setFieldsValue(inventoryRooms);

    //We don't want this to re-run on every form change
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy directive, needs investigation
  }, [inventoryRooms]);

  const handleFieldsChange = useCallback(
    (changedFields: FieldData[]) => {
      if (!rawInventory || !dependencies) {
        return;
      }

      const retrieved = retrieveDataFromNamePath(changedFields[0]?.name);

      if (!retrieved) {
        return;
      }

      const { dateKey, itemId, field } = retrieved;

      const existing = modifiedInventory?.[dateKey]?.rooms?.[Number(itemId)];

      if (!existing) {
        return;
      }

      if (!isEnabledByMessage(existing.message)) {
        return;
      }

      const editedField = mapFormFieldToEditedField(field);

      if (editedField) {
        addEditedRoomField(dateKey, Number(itemId), editedField);
      }

      let currentInventory = modifiedInventory ?? {};

      const prevEntry = currentInventory[dateKey]?.rooms?.[Number(itemId)] as
        Record<string, unknown> | undefined;

      currentInventory = updateRoomFieldsForDate(
        currentInventory ?? {},
        Number(itemId),
        dateKey,
        { [field]: changedFields[0]?.value },
        dependencies
      );

      const newEntry = currentInventory[dateKey]?.rooms?.[Number(itemId)] as
        Record<string, unknown> | undefined;

      const formFields = getChangedFormFields(
        prevEntry,
        newEntry,
        dateKey,
        itemId
      );

      setModifiedInventory(currentInventory);

      const prevMode = prevEntry?.discountMode as
        RoomOpeningDiscountMode | undefined;
      const newMode = newEntry?.discountMode as
        RoomOpeningDiscountMode | undefined;
      const hasDeactivatedRule =
        (prevMode === ROOM_OPENING_DISCOUNT_MODE.AUTO ||
          prevMode === ROOM_OPENING_DISCOUNT_MODE.LAST_MINUTE_AUTO) &&
        (newMode === ROOM_OPENING_DISCOUNT_MODE.MANUAL ||
          newMode === ROOM_OPENING_DISCOUNT_MODE.NONE);

      if (hasDeactivatedRule) {
        setHasDeactivatedPricingRuleByEdit(true);
      }

      if (formFields.length > 0) {
        storeForm?.setFields(formFields);
      }
    },
    [
      rawInventory,
      dependencies,
      modifiedInventory,
      storeForm,
      setModifiedInventory,
      addEditedRoomField,
      setHasDeactivatedPricingRuleByEdit,
    ]
  );

  const saveInventory = async (values: InventoryRooms) => {
    const { inventoryRooms: roomTimePeriodStore } =
      retrieveFormValues(timePeriodStore);

    const modifiedValues = getModifiedValues(
      { ...values, ...roomTimePeriodStore },
      inventoryRooms
    );

    const { editedRoomFields, clearEditedRoomFields } =
      inventoryStore.getState();

    await saveRoomInventory(
      mapToRoomResponse(modifiedValues, editedRoomFields)
    );

    clearEditedRoomFields();
  };

  const onFinish = async (values: InventoryRooms) => {
    if (hasDeactivatedPricingRuleByEdit) {
      setPendingSaveValues(values);

      return;
    }

    await saveInventory(values);
  };

  const handleConfirmPricingRuleDeactivation = async () => {
    if (pendingSaveValues) {
      await saveInventory(pendingSaveValues);
    }

    setPendingSaveValues(null);
    setHasDeactivatedPricingRuleByEdit(false);
  };

  const handleCancelPricingRuleDeactivation = () => {
    setPendingSaveValues(null);

    setIsEditMode(true);
  };

  if (isFetching && !!inventory) {
    return <RoomTabSkeleton />;
  }

  return (
    <Form
      form={form}
      className={classNames(styles.roomInventoryForm, {
        [styles.noPadding]: sortedRooms.length === 0,
      })}
      layout="vertical"
      onFinish={onFinish}
      initialValues={inventoryRooms}
      onFieldsChange={handleFieldsChange}
    >
      <PricingRuleAlertModal
        isOpen={pendingSaveValues !== null}
        onConfirm={handleConfirmPricingRuleDeactivation}
        onCancel={handleCancelPricingRuleDeactivation}
      />

      {sortedRooms.map((room) => {
        const categoryName = roomsCategories?.find(
          (cat) => cat.id === room.categoryId
        )?.name;

        return (
          <RoomTableItem
            key={room.id}
            room={room}
            categoryName={categoryName}
          />
        );
      })}

      {sortedRooms.length <= 1 && (
        <EmptyState
          title="Create new rooms"
          hideSubtitle
          hasBorder={sortedRooms.length > 0}
        />
      )}
    </Form>
  );
};
